import { Controller } from '@hotwired/stimulus';
import { UAParser } from 'ua-parser-js';

export default class extends Controller {
  connect() {
    // ensure all platform-specific elements are hidden before detection
    this.element.querySelectorAll('[data-platform-sniffer-platform-param]').forEach((el) => {
      el.classList.add('hidden', 'opacity-0');
      el.classList.remove('animate-fade-in-now');
    });
    this.element.querySelectorAll('[data-platform-sniffer-platform-param][data-platform-sniffer-arch-param]').forEach((el) => {
      el.classList.add('hidden', 'opacity-0');
      el.classList.remove('animate-fade-in-now');
    });

    // then detect and update
    this.detectAndUpdate();
  }

  async detectAndUpdate() {
    const platform = await this.detectPlatform();
    const arch = await this.detectArchitecture();

    // update images - show matching platform with fade-in
    const images = this.element.querySelectorAll('[data-platform-sniffer-platform-param]:not([data-platform-sniffer-arch-param])');
    images.forEach((img) => {
      if (img.dataset.platformSnifferPlatformParam === platform) {
        img.classList.remove('hidden', 'opacity-0');
        img.classList.add('animate-fade-in-now');
      } else {
        img.classList.add('hidden', 'opacity-0');
        img.classList.remove('animate-fade-in-now');
      }
    });

    // update buttons - show matching platform and arch with fade-in (only if arch is known)
    const buttons = this.element.querySelectorAll('[data-platform-sniffer-platform-param][data-platform-sniffer-arch-param]');
    buttons.forEach((btn) => {
      if (arch && btn.dataset.platformSnifferPlatformParam === platform && btn.dataset.platformSnifferArchParam === arch) {
        btn.classList.remove('hidden', 'opacity-0');
        btn.classList.add('animate-fade-in-now');
      } else {
        btn.classList.add('hidden', 'opacity-0');
        btn.classList.remove('animate-fade-in-now');
      }
    });
  }

  async detectPlatform() {
    // check user agent data api first (modern browsers)
    if (navigator.userAgentData) {
      try {
        const highEntropy = await navigator.userAgentData.getHighEntropyValues(['platform']);
        if (highEntropy.platform) {
          const platform = highEntropy.platform.toLowerCase();
          if (platform.includes('mac')) return 'darwin';
          if (platform.includes('win')) return 'win32';
        }
      } catch {
        // fall through to fallback detection if api fails
      }
    }

    // use ua-parser-js for better user agent parsing
    const parser = new UAParser();
    const os = parser.getOS();
    if (os.name) {
      const osName = os.name.toLowerCase();
      if (osName.includes('mac')) return 'darwin';
      if (osName.includes('windows')) return 'win32';
    }

    // fallback to navigator.platform or user agent
    const platform = (navigator.platform || navigator.userAgent).toLowerCase();
    if (platform.includes('mac')) return 'darwin';
    if (platform.includes('win')) return 'win32';

    return 'darwin';
  }

  async detectArchitecture() {
    // check user agent data api first (most reliable)
    if (navigator.userAgentData) {
      try {
        const highEntropy = await navigator.userAgentData.getHighEntropyValues(['architecture']);
        if (highEntropy.architecture) {
          if (highEntropy.architecture === 'arm') return 'arm64';
          if (highEntropy.architecture === 'x86') return 'x64';
        }
      } catch {
        // fall through to fallback detection if api fails
      }
    }

    // use ua-parser-js for cpu detection
    const parser = new UAParser();
    const cpu = parser.getCPU();
    if (cpu.architecture) {
      const arch = cpu.architecture.toLowerCase();
      if (arch.includes('arm') || arch.includes('aarch')) return 'arm64';
      if (arch.includes('x64') || arch.includes('x86_64') || arch.includes('amd64')) return 'x64';
    }

    // check user agent for architecture hints
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('arm64') || ua.includes('aarch64')) return 'arm64';
    if (ua.includes('x64') || ua.includes('x86_64') || ua.includes('wow64')) return 'x64';

    // platform-specific detection
    const platform = await this.detectPlatform();
    if (platform === 'darwin') {
      // webgl-based detection for macs (safari doesn't support userAgentData)
      const webglArch = this.detectArchitectureViaWebGL();
      if (webglArch) return webglArch;
    }

    // return null if architecture cannot be determined
    return null;
  }

  detectArchitectureViaWebGL() {
    // webgl can detect gpu type to infer architecture on macs
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return null;

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return null;

      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      if (!renderer) return null;

      // apple silicon macs use apple gpu
      if (/Apple GPU/i.test(renderer)) return 'arm64';
      // intel macs use intel or amd gpu
      if (/Intel|AMD/i.test(renderer)) return 'x64';
    } catch {
      // webgl detection failed
    }
    return null;
  }
}
